import { generateCode, analyzeTextNLP } from '../services/api';
import { toast } from 'sonner';

export interface ApplicationRequirements {
  description: string;
  domain: string;
  userType: 'individual' | 'business' | 'enterprise';
  features: string[];
  technologies: {
    frontend: string[];
    backend: string[];
    database: string[];
    deployment: string[];
  };
  constraints: {
    budget?: number;
    timeline?: string;
    scalability?: 'low' | 'medium' | 'high';
    security?: 'basic' | 'standard' | 'enterprise';
    compliance?: string[];
  };
  targetPlatforms: ('web' | 'mobile' | 'desktop' | 'api')[];
}

export interface GeneratedApplication {
  id: string;
  name: string;
  description: string;
  architecture: ApplicationArchitecture;
  database: DatabaseSchema;
  apis: APIBlueprint[];
  frontend: FrontendBlueprint;
  deployment: DeploymentConfig;
  documentation: ApplicationDocumentation;
  estimatedCost: number;
  estimatedTimeline: string;
  confidence: number;
  generatedAt: Date;
}

export interface ApplicationArchitecture {
  pattern: 'monolithic' | 'microservices' | 'serverless' | 'hybrid';
  components: ArchitectureComponent[];
  dataFlow: DataFlowDiagram;
  scalabilityStrategy: string;
  securityArchitecture: SecurityBlueprint;
}

export interface ArchitectureComponent {
  name: string;
  type: 'frontend' | 'backend' | 'database' | 'api' | 'service' | 'middleware';
  technology: string;
  responsibilities: string[];
  dependencies: string[];
  scalability: 'low' | 'medium' | 'high';
}

export interface DataFlowDiagram {
  entities: DataEntity[];
  relationships: DataRelationship[];
  workflows: Workflow[];
}

export interface DataEntity {
  name: string;
  type: 'user' | 'data' | 'external' | 'system';
  attributes: string[];
  relationships: string[];
}

export interface DataRelationship {
  from: string;
  to: string;
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  description: string;
}

export interface Workflow {
  name: string;
  steps: WorkflowStep[];
  triggers: string[];
  conditions: string[];
}

export interface WorkflowStep {
  name: string;
  action: string;
  component: string;
  inputs: string[];
  outputs: string[];
}

export interface SecurityBlueprint {
  authentication: string;
  authorization: string;
  encryption: string;
  compliance: string[];
  securityFeatures: string[];
}

export interface DatabaseSchema {
  type: 'relational' | 'document' | 'graph' | 'hybrid';
  tables: DatabaseTable[];
  relationships: DatabaseRelationship[];
  indexes: DatabaseIndex[];
  migrations: DatabaseMigration[];
}

export interface DatabaseTable {
  name: string;
  columns: DatabaseColumn[];
  primaryKey: string;
  indexes: string[];
  constraints: string[];
}

export interface DatabaseColumn {
  name: string;
  type: string;
  nullable: boolean;
  defaultValue?: any;
  constraints: string[];
}

export interface DatabaseRelationship {
  name: string;
  fromTable: string;
  toTable: string;
  fromColumn: string;
  toColumn: string;
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  onDelete: 'cascade' | 'set-null' | 'restrict';
}

export interface DatabaseIndex {
  name: string;
  table: string;
  columns: string[];
  type: 'btree' | 'hash' | 'gin' | 'gist';
  unique: boolean;
}

export interface DatabaseMigration {
  version: string;
  description: string;
  up: string;
  down: string;
}

export interface APIBlueprint {
  name: string;
  version: string;
  basePath: string;
  endpoints: APIEndpoint[];
  models: APIModel[];
  authentication: APIAuth;
  documentation: APIDocumentation;
}

export interface APIEndpoint {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  summary: string;
  description: string;
  parameters: APIParameter[];
  requestBody?: APIRequestBody;
  responses: { [statusCode: number]: APIResponse };
  security: string[];
  tags: string[];
}

export interface APIParameter {
  name: string;
  in: 'path' | 'query' | 'header' | 'cookie';
  required: boolean;
  schema: APISchema;
  description: string;
}

export interface APIRequestBody {
  required: boolean;
  content: { [mediaType: string]: APIMediaType };
}

export interface APIMediaType {
  schema: APISchema;
  example?: any;
}

export interface APIResponse {
  statusCode: number;
  description: string;
  content?: { [mediaType: string]: APIMediaType };
  headers?: { [name: string]: APIHeader };
}

export interface APIHeader {
  description: string;
  schema: APISchema;
}

export interface APISchema {
  type?: string; // Made optional for $ref usage
  format?: string;
  properties?: { [name: string]: APISchema };
  items?: APISchema;
  required?: string[];
  enum?: any[];
  description?: string;
  default?: any; // OpenAPI default value
  $ref?: string; // OpenAPI reference
  oneOf?: APISchema[]; // OpenAPI oneOf
  allOf?: APISchema[]; // OpenAPI allOf
  anyOf?: APISchema[]; // OpenAPI anyOf
}

export interface APIModel {
  name: string;
  type: 'request' | 'response' | 'data';
  schema: APISchema;
  examples: any[];
}

export interface APIAuth {
  type: 'jwt' | 'oauth2' | 'api-key' | 'basic';
  flows?: OAuthFlow[];
  securitySchemes: { [name: string]: SecurityScheme };
}

export interface OAuthFlow {
  authorizationUrl: string;
  tokenUrl: string;
  scopes: { [scope: string]: string };
}

export interface SecurityScheme {
  type: 'apiKey' | 'http' | 'oauth2' | 'openIdConnect';
  description?: string;
  name?: string;
  in?: 'query' | 'header' | 'cookie';
  scheme?: string;
  bearerFormat?: string;
  flows?: OAuthFlow[];
  openIdConnectUrl?: string;
}

export interface APIDocumentation {
  title: string;
  version: string;
  description: string;
  termsOfService?: string;
  contact?: APIContact;
  license?: APILicense;
  servers: APIServer[];
}

export interface APIContact {
  name?: string;
  url?: string;
  email?: string;
}

export interface APILicense {
  name: string;
  url?: string;
}

export interface APIServer {
  url: string;
  description?: string;
  variables?: { [name: string]: APIServerVariable };
}

export interface APIServerVariable {
  default: string;
  enum?: string[];
  description?: string;
}

export interface FrontendBlueprint {
  framework: string;
  components: FrontendComponent[];
  pages: FrontendPage[];
  routing: FrontendRouting;
  stateManagement: FrontendState;
  styling: FrontendStyling;
  buildConfig: FrontendBuild;
}

export interface FrontendComponent {
  name: string;
  type: 'layout' | 'form' | 'list' | 'detail' | 'navigation' | 'modal' | 'chart';
  props: FrontendProp[];
  children?: string[];
  styling: string;
  logic: string;
}

export interface FrontendProp {
  name: string;
  type: string;
  required: boolean;
  defaultValue?: any;
  description: string;
}

export interface FrontendPage {
  name: string;
  route: string;
  layout: string;
  components: string[];
  dataRequirements: string[];
  permissions: string[];
}

export interface FrontendRouting {
  type: 'client-side' | 'server-side' | 'hybrid';
  routes: FrontendRoute[];
  guards: FrontendGuard[];
}

export interface FrontendRoute {
  path: string;
  component: string;
  children?: FrontendRoute[];
  guards?: string[];
  data?: any;
}

export interface FrontendGuard {
  name: string;
  logic: string;
  redirectTo?: string;
}

export interface FrontendState {
  type: 'redux' | 'zustand' | 'context' | 'mobx' | 'none';
  stores: FrontendStore[];
  actions: FrontendAction[];
}

export interface FrontendStore {
  name: string;
  state: { [key: string]: any };
  actions: string[];
}

export interface FrontendAction {
  name: string;
  type: string;
  payload?: any;
  reducer: string;
}

export interface FrontendStyling {
  framework: 'css' | 'scss' | 'styled-components' | 'tailwind' | 'css-modules';
  theme: FrontendTheme;
  globalStyles: string;
  componentStyles: { [component: string]: string };
}

export interface FrontendTheme {
  colors: { [name: string]: string };
  typography: { [name: string]: any };
  spacing: { [name: string]: string };
  breakpoints: { [name: string]: string };
}

export interface FrontendBuild {
  bundler: 'webpack' | 'vite' | 'rollup' | 'esbuild';
  config: any;
  scripts: { [name: string]: string };
  optimizations: string[];
}

export interface DeploymentConfig {
  platform: 'docker' | 'kubernetes' | 'aws' | 'azure' | 'gcp' | 'heroku' | 'vercel';
  environment: 'development' | 'staging' | 'production';
  services: DeploymentService[];
  infrastructure: DeploymentInfrastructure;
  monitoring: DeploymentMonitoring;
  scaling: DeploymentScaling;
}

export interface DeploymentService {
  name: string;
  image: string;
  ports: number[];
  environment: { [key: string]: string };
  volumes: string[];
  dependsOn: string[];
  healthcheck: DeploymentHealthcheck;
}

export interface DeploymentHealthcheck {
  test: string[];
  interval: string;
  timeout: string;
  retries: number;
  startPeriod: string;
}

export interface DeploymentInfrastructure {
  network: string;
  volumes: string[];
  secrets: string[];
  configs: { [key: string]: string };
}

export interface DeploymentMonitoring {
  logging: DeploymentLogging;
  metrics: DeploymentMetrics;
  tracing: DeploymentTracing;
  alerting: DeploymentAlerting;
}

export interface DeploymentLogging {
  driver: string;
  options: { [key: string]: string };
  format: string;
}

export interface DeploymentMetrics {
  provider: 'prometheus' | 'datadog' | 'newrelic' | 'cloudwatch';
  endpoints: string[];
  customMetrics: string[];
}

export interface DeploymentTracing {
  provider: 'jaeger' | 'zipkin' | 'datadog' | 'aws-xray';
  samplingRate: number;
  serviceName: string;
}

export interface DeploymentAlerting {
  provider: 'slack' | 'email' | 'pagerduty' | 'webhook';
  channels: string[];
  rules: DeploymentAlertRule[];
}

export interface DeploymentAlertRule {
  name: string;
  condition: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  channels: string[];
}

export interface DeploymentScaling {
  type: 'horizontal' | 'vertical' | 'auto';
  minInstances: number;
  maxInstances: number;
  targetCPU: number;
  targetMemory: number;
  scalingRules: DeploymentScalingRule[];
}

export interface DeploymentScalingRule {
  metric: string;
  operator: '>' | '<' | '>=' | '<=';
  threshold: number;
  action: 'scale-up' | 'scale-down';
  cooldown: number;
}

export interface ApplicationDocumentation {
  readme: string;
  apiDocs: string;
  deploymentGuide: string;
  architectureDiagram: string;
  userGuide: string;
  developerGuide: string;
  securityGuide: string;
  performanceGuide: string;
}

export class AutonomousApplicationGenerator {
  private generationHistory: Map<string, GeneratedApplication> = new Map();

  async generateApplication(requirements: ApplicationRequirements): Promise<GeneratedApplication> {
    const startTime = Date.now();

    try {
      // Step 1: Analyze requirements
      const analysis = await this.analyzeRequirements(requirements);

      // Step 2: Generate application architecture
      const architecture = await this.generateArchitecture(analysis);

      // Step 3: Create database schema
      const database = await this.generateDatabaseSchema(analysis, architecture);

      // Step 4: Design APIs
      const apis = await this.generateAPIs(analysis, architecture, database);

      // Step 5: Generate frontend
      const frontend = await this.generateFrontend(analysis, architecture, apis);

      // Step 6: Create deployment configuration
      const deployment = await this.generateDeployment(analysis, architecture);

      // Step 7: Generate documentation
      const documentation = await this.generateDocumentation(analysis, architecture, apis, frontend, deployment);

      // Step 8: Calculate estimates
      const estimates = await this.calculateEstimates(analysis, architecture);

      const application: GeneratedApplication = {
        id: `app_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: this.generateApplicationName(requirements),
        description: requirements.description,
        architecture,
        database,
        apis,
        frontend,
        deployment,
        documentation,
        estimatedCost: estimates.cost,
        estimatedTimeline: estimates.timeline,
        confidence: analysis.confidence,
        generatedAt: new Date()
      };

      this.generationHistory.set(application.id, application);

      const generationTime = Date.now() - startTime;
      toast.success(`Application generated in ${Math.round(generationTime / 1000)}s with ${Math.round(analysis.confidence * 100)}% confidence`);

      return application;

    } catch (error) {
      console.error('Application generation failed:', error);
      toast.error('Failed to generate application. Please try again.');
      throw error;
    }
  }

  private async analyzeRequirements(requirements: ApplicationRequirements): Promise<{
    features: string[];
    complexity: number;
    domains: string[];
    technologies: any;
    confidence: number;
  }> {
    // Analyze the natural language description
    const nlpAnalysis = await analyzeTextNLP(requirements.description);

    // Extract key features and requirements
    const features = this.extractFeatures(requirements);
    const complexity = this.assessComplexity(requirements, features);
    const domains = this.identifyDomains(requirements, features);

    // Validate technology choices
    const technologies = this.validateTechnologies(requirements.technologies);

    // Calculate confidence based on clarity and specificity
    const confidence = this.calculateAnalysisConfidence(requirements, features);

    return {
      features,
      complexity,
      domains,
      technologies,
      confidence
    };
  }

  private async generateArchitecture(analysis: any): Promise<ApplicationArchitecture> {
    const pattern = this.determineArchitecturePattern(analysis);

    const components = await this.generateComponents(analysis, pattern);

    const dataFlow = await this.generateDataFlow(analysis, components);

    const scalabilityStrategy = this.determineScalabilityStrategy(analysis);

    const securityArchitecture = this.generateSecurityBlueprint(analysis);

    return {
      pattern,
      components,
      dataFlow,
      scalabilityStrategy,
      securityArchitecture
    };
  }

  private async generateDatabaseSchema(analysis: any, architecture: ApplicationArchitecture): Promise<DatabaseSchema> {
    const type = this.determineDatabaseType(analysis);

    const tables = await this.generateDatabaseTables(analysis, architecture);

    const relationships = this.generateDatabaseRelationships(tables);

    const indexes = this.generateDatabaseIndexes(tables, analysis);

    const migrations = this.generateDatabaseMigrations(tables, relationships, indexes);

    return {
      type,
      tables,
      relationships,
      indexes,
      migrations
    };
  }

  private async generateAPIs(analysis: any, architecture: ApplicationArchitecture, database: DatabaseSchema): Promise<APIBlueprint[]> {
    const apis: APIBlueprint[] = [];

    // Generate main API
    const mainApi = await this.generateMainAPI(analysis, architecture, database);
    apis.push(mainApi);

    // Generate additional service APIs if microservices
    if (architecture.pattern === 'microservices') {
      const serviceApis = await this.generateServiceAPIs(architecture, database);
      apis.push(...serviceApis);
    }

    return apis;
  }

  private async generateFrontend(analysis: any, architecture: ApplicationArchitecture, apis: APIBlueprint[]): Promise<FrontendBlueprint> {
    const framework = this.determineFrontendFramework(analysis);

    const components = await this.generateFrontendComponents(analysis, apis);

    const pages = await this.generateFrontendPages(analysis, components);

    const routing = this.generateFrontendRouting(pages);

    const stateManagement = this.generateStateManagement(analysis, framework);

    const styling = this.generateFrontendStyling(framework);

    const buildConfig = this.generateFrontendBuild(framework);

    return {
      framework,
      components,
      pages,
      routing,
      stateManagement,
      styling,
      buildConfig
    };
  }

  private async generateDeployment(analysis: any, architecture: ApplicationArchitecture): Promise<DeploymentConfig> {
    const platform = this.determineDeploymentPlatform(analysis);

    const services = await this.generateDeploymentServices(architecture, platform);

    const infrastructure = this.generateDeploymentInfrastructure(architecture, platform);

    const monitoring = this.generateDeploymentMonitoring(platform);

    const scaling = this.generateDeploymentScaling(analysis, architecture);

    return {
      platform,
      environment: 'development',
      services,
      infrastructure,
      monitoring,
      scaling
    };
  }

  private async generateDocumentation(analysis: any, architecture: ApplicationArchitecture, apis: APIBlueprint[], frontend: FrontendBlueprint, deployment: DeploymentConfig): Promise<ApplicationDocumentation> {
    const readme = await this.generateReadme(analysis);
    const apiDocs = this.generateAPIDocs(apis);
    const deploymentGuide = this.generateDeploymentGuide(deployment);
    const architectureDiagram = this.generateArchitectureDiagram(architecture);
    const userGuide = await this.generateUserGuide(analysis);
    const developerGuide = this.generateDeveloperGuide(architecture, frontend);
    const securityGuide = this.generateSecurityGuide(architecture);
    const performanceGuide = this.generatePerformanceGuide(architecture, deployment);

    return {
      readme,
      apiDocs,
      deploymentGuide,
      architectureDiagram,
      userGuide,
      developerGuide,
      securityGuide,
      performanceGuide
    };
  }

  private async calculateEstimates(analysis: any, architecture: ApplicationArchitecture): Promise<{
    cost: number;
    timeline: string;
  }> {
    const complexity = analysis.complexity;
    const componentCount = architecture.components.length;

    // Base estimates
    let baseCost = 5000; // Base cost in USD
    let baseWeeks = 4;

    // Adjust for complexity
    baseCost *= (1 + complexity * 0.5);
    baseWeeks *= (1 + complexity * 0.3);

    // Adjust for architecture
    if (architecture.pattern === 'microservices') {
      baseCost *= 1.5;
      baseWeeks *= 1.3;
    } else if (architecture.pattern === 'serverless') {
      baseCost *= 0.8;
      baseWeeks *= 0.7;
    }

    // Adjust for component count
    baseCost *= (1 + componentCount * 0.1);
    baseWeeks *= (1 + componentCount * 0.05);

    return {
      cost: Math.round(baseCost),
      timeline: `${Math.ceil(baseWeeks)} weeks`
    };
  }

  // Helper methods
  private extractFeatures(requirements: ApplicationRequirements): string[] {
    const features = [...requirements.features];

    // Extract additional features from description using NLP
    const description = requirements.description.toLowerCase();

    if (description.includes('user') && description.includes('login')) {
      features.push('User Authentication');
    }
    if (description.includes('dashboard') || description.includes('analytics')) {
      features.push('Dashboard & Analytics');
    }
    if (description.includes('search')) {
      features.push('Search Functionality');
    }
    if (description.includes('notification') || description.includes('alert')) {
      features.push('Notifications');
    }
    if (description.includes('payment') || description.includes('billing')) {
      features.push('Payment Processing');
    }
    if (description.includes('admin') || description.includes('management')) {
      features.push('Admin Panel');
    }

    return [...new Set(features)]; // Remove duplicates
  }

  private assessComplexity(requirements: ApplicationRequirements, features: string[]): number {
    let complexity = 0.3; // Base complexity

    // Factor in feature count
    complexity += Math.min(features.length * 0.05, 0.3);

    // Factor in technology choices
    if (requirements.technologies.frontend.length > 2) complexity += 0.1;
    if (requirements.technologies.backend.length > 2) complexity += 0.1;
    if (requirements.technologies.database.length > 1) complexity += 0.1;

    // Factor in target platforms
    complexity += requirements.targetPlatforms.length * 0.05;

    // Factor in constraints
    if (requirements.constraints.security === 'enterprise') complexity += 0.2;
    if (requirements.constraints.scalability === 'high') complexity += 0.15;

    return Math.min(complexity, 1.0);
  }

  private identifyDomains(requirements: ApplicationRequirements, features: string[]): string[] {
    const domains = new Set<string>();

    const description = requirements.description.toLowerCase();

    if (description.includes('ecommerce') || description.includes('shop') || description.includes('store')) {
      domains.add('ecommerce');
    }
    if (description.includes('health') || description.includes('medical') || description.includes('patient')) {
      domains.add('healthcare');
    }
    if (description.includes('finance') || description.includes('bank') || description.includes('payment')) {
      domains.add('finance');
    }
    if (description.includes('education') || description.includes('learning') || description.includes('course')) {
      domains.add('education');
    }
    if (description.includes('social') || description.includes('community') || description.includes('network')) {
      domains.add('social');
    }
    if (description.includes('business') || description.includes('enterprise') || description.includes('company')) {
      domains.add('enterprise');
    }

    // Default to general if no specific domain identified
    if (domains.size === 0) {
      domains.add('general');
    }

    return Array.from(domains);
  }

  private validateTechnologies(technologies: any): any {
    // Validate and provide defaults for technology choices
    return {
      frontend: technologies.frontend.length > 0 ? technologies.frontend : ['react', 'typescript'],
      backend: technologies.backend.length > 0 ? technologies.backend : ['nodejs', 'express'],
      database: technologies.database.length > 0 ? technologies.database : ['postgresql'],
      deployment: technologies.deployment.length > 0 ? technologies.deployment : ['docker']
    };
  }

  private calculateAnalysisConfidence(requirements: ApplicationRequirements, features: string[]): number {
    let confidence = 0.5; // Base confidence

    // Higher confidence for detailed descriptions
    if (requirements.description.length > 100) confidence += 0.1;
    if (requirements.description.length > 500) confidence += 0.1;

    // Higher confidence for specific technology choices
    if (requirements.technologies.frontend.length > 0) confidence += 0.1;
    if (requirements.technologies.backend.length > 0) confidence += 0.1;
    if (requirements.technologies.database.length > 0) confidence += 0.1;

    // Higher confidence for specific features
    if (features.length > 3) confidence += 0.1;

    // Lower confidence for vague or conflicting requirements
    if (requirements.description.toLowerCase().includes('flexible') ||
        requirements.description.toLowerCase().includes('whatever')) {
      confidence -= 0.1;
    }

    return Math.max(0.1, Math.min(confidence, 0.95));
  }

  private determineArchitecturePattern(analysis: any): ApplicationArchitecture['pattern'] {
    const { complexity, features } = analysis;

    if (complexity > 0.7 || features.length > 10) {
      return 'microservices';
    } else if (features.some((f: string) => f.toLowerCase().includes('serverless') || f.toLowerCase().includes('event'))) {
      return 'serverless';
    } else if (complexity > 0.4) {
      return 'hybrid';
    } else {
      return 'monolithic';
    }
  }

  private async generateComponents(analysis: any, pattern: ApplicationArchitecture['pattern']): Promise<ArchitectureComponent[]> {
    const components: ArchitectureComponent[] = [];

    // Always include frontend and backend
    components.push({
      name: 'frontend',
      type: 'frontend',
      technology: analysis.technologies.frontend[0] || 'react',
      responsibilities: ['User Interface', 'User Experience', 'Client-side Logic'],
      dependencies: ['backend'],
      scalability: 'medium'
    });

    components.push({
      name: 'backend',
      type: 'backend',
      technology: analysis.technologies.backend[0] || 'nodejs',
      responsibilities: ['Business Logic', 'API Endpoints', 'Data Processing'],
      dependencies: ['database'],
      scalability: 'high'
    });

    components.push({
      name: 'database',
      type: 'database',
      technology: analysis.technologies.database[0] || 'postgresql',
      responsibilities: ['Data Storage', 'Data Retrieval', 'Data Integrity'],
      dependencies: [],
      scalability: 'high'
    });

    // Add additional components based on pattern and features
    if (pattern === 'microservices' || analysis.features.some((f: string) => f.toLowerCase().includes('auth'))) {
      components.push({
        name: 'auth-service',
        type: 'service',
        technology: analysis.technologies.backend[0] || 'nodejs',
        responsibilities: ['User Authentication', 'Authorization', 'Token Management'],
        dependencies: ['database'],
        scalability: 'medium'
      });
    }

    if (analysis.features.some((f: string) => f.toLowerCase().includes('notification') || f.toLowerCase().includes('email'))) {
      components.push({
        name: 'notification-service',
        type: 'service',
        technology: analysis.technologies.backend[0] || 'nodejs',
        responsibilities: ['Email Notifications', 'Push Notifications', 'SMS'],
        dependencies: ['database'],
        scalability: 'medium'
      });
    }

    return components;
  }

  private async generateDataFlow(analysis: any, components: ArchitectureComponent[]): Promise<DataFlowDiagram> {
    // Generate data flow based on components and features
    const entities = this.generateDataEntities(analysis);
    const relationships = this.generateDataRelationships(entities);
    const workflows = this.generateWorkflows(analysis, components);

    return {
      entities,
      relationships,
      workflows
    };
  }

  private generateDataEntities(analysis: any): DataEntity[] {
    const entities: DataEntity[] = [
      {
        name: 'User',
        type: 'user',
        attributes: ['id', 'email', 'name', 'created_at', 'updated_at'],
        relationships: ['sessions', 'permissions']
      }
    ];

    // Add entities based on features
    if (analysis.features.some((f: string) => f.toLowerCase().includes('product') || f.toLowerCase().includes('item'))) {
      entities.push({
        name: 'Product',
        type: 'data',
        attributes: ['id', 'name', 'description', 'price', 'category', 'created_at'],
        relationships: ['orders', 'inventory']
      });
    }

    if (analysis.features.some((f: string) => f.toLowerCase().includes('order') || f.toLowerCase().includes('purchase'))) {
      entities.push({
        name: 'Order',
        type: 'data',
        attributes: ['id', 'user_id', 'total', 'status', 'created_at'],
        relationships: ['user', 'order_items']
      });
    }

    return entities;
  }

  private generateDataRelationships(entities: DataEntity[]): DataRelationship[] {
    const relationships: DataRelationship[] = [];

    // Generate relationships based on entity attributes
    for (const entity of entities) {
      for (const relationship of entity.relationships) {
        const relatedEntity = entities.find(e => e.name.toLowerCase() === relationship.replace('_id', '').toLowerCase());
        if (relatedEntity) {
          relationships.push({
            from: entity.name,
            to: relatedEntity.name,
            type: relationship.includes('s') ? 'one-to-many' : 'one-to-one',
            description: `${entity.name} ${relationship.includes('s') ? 'has many' : 'belongs to'} ${relatedEntity.name}`
          });
        }
      }
    }

    return relationships;
  }

  private generateWorkflows(analysis: any, components: ArchitectureComponent[]): Workflow[] {
    const workflows: Workflow[] = [];

    // Generate basic CRUD workflow
    workflows.push({
      name: 'User Registration',
      steps: [
        {
          name: 'Validate Input',
          action: 'validate_user_data',
          component: 'frontend',
          inputs: ['email', 'password', 'name'],
          outputs: ['validation_result']
        },
        {
          name: 'Create User',
          action: 'create_user',
          component: 'backend',
          inputs: ['validated_data'],
          outputs: ['user_id', 'confirmation_token']
        },
        {
          name: 'Send Confirmation',
          action: 'send_email',
          component: 'notification-service',
          inputs: ['user_email', 'confirmation_token'],
          outputs: ['email_sent']
        }
      ],
      triggers: ['user_registration_request'],
      conditions: ['email_not_exists', 'valid_password']
    });

    // Add more workflows based on features
    if (analysis.features.some((f: string) => f.toLowerCase().includes('order'))) {
      workflows.push({
        name: 'Order Processing',
        steps: [
          {
            name: 'Validate Order',
            action: 'validate_order',
            component: 'backend',
            inputs: ['order_data'],
            outputs: ['validation_result']
          },
          {
            name: 'Process Payment',
            action: 'process_payment',
            component: 'payment-service',
            inputs: ['order_total', 'payment_info'],
            outputs: ['payment_result']
          },
          {
            name: 'Update Inventory',
            action: 'update_inventory',
            component: 'backend',
            inputs: ['order_items'],
            outputs: ['inventory_updated']
          }
        ],
        triggers: ['order_submission'],
        conditions: ['payment_successful', 'inventory_available']
      });
    }

    return workflows;
  }

  private determineScalabilityStrategy(analysis: any): string {
    if (analysis.constraints?.scalability === 'high') {
      return 'Horizontal scaling with load balancers, auto-scaling groups, and CDN integration';
    } else if (analysis.constraints?.scalability === 'medium') {
      return 'Vertical scaling with caching layers and optimized database queries';
    } else {
      return 'Basic scaling with monitoring and manual optimization opportunities';
    }
  }

  private generateSecurityBlueprint(analysis: any): SecurityBlueprint {
    const securityLevel = analysis.constraints?.security || 'basic';

    const features = [];
    if (securityLevel === 'enterprise') {
      features.push('OAuth 2.0 + OpenID Connect', 'Role-based access control', 'End-to-end encryption', 'Audit logging', 'Security monitoring');
    } else if (securityLevel === 'standard') {
      features.push('JWT authentication', 'Input validation', 'HTTPS encryption', 'Basic audit logging');
    } else {
      features.push('Basic authentication', 'Input sanitization', 'HTTPS encryption');
    }

    return {
      authentication: securityLevel === 'enterprise' ? 'OAuth 2.0' : 'JWT',
      authorization: securityLevel === 'enterprise' ? 'RBAC + ABAC' : 'Basic permissions',
      encryption: 'AES-256 for data at rest, TLS 1.3 for data in transit',
      compliance: analysis.constraints?.compliance || [],
      securityFeatures: features
    };
  }

  private determineDatabaseType(analysis: any): DatabaseSchema['type'] {
    const features = analysis.features;

    if (features.some((f: string) => f.toLowerCase().includes('graph') || f.toLowerCase().includes('relationship'))) {
      return 'graph';
    } else if (features.some((f: string) => f.toLowerCase().includes('document') || f.toLowerCase().includes('flexible'))) {
      return 'document';
    } else if (features.some((f: string) => f.toLowerCase().includes('hybrid') || f.toLowerCase().includes('complex'))) {
      return 'hybrid';
    } else {
      return 'relational';
    }
  }

  private async generateDatabaseTables(analysis: any, architecture: ApplicationArchitecture): Promise<DatabaseTable[]> {
    const tables: DatabaseTable[] = [];

    // Users table (always included)
    tables.push({
      name: 'users',
      columns: [
        { name: 'id', type: 'uuid', nullable: false, constraints: [] },
        { name: 'email', type: 'varchar(255)', nullable: false, constraints: ['unique'] },
        { name: 'password_hash', type: 'varchar(255)', nullable: false, constraints: [] },
        { name: 'full_name', type: 'varchar(255)', nullable: true, constraints: [] },
        { name: 'created_at', type: 'timestamp', nullable: false, defaultValue: 'NOW()', constraints: [] },
        { name: 'updated_at', type: 'timestamp', nullable: false, defaultValue: 'NOW()', constraints: [] }
      ],
      primaryKey: 'id',
      indexes: ['email'],
      constraints: ['unique(email)']
    });

    // Add tables based on features
    if (analysis.features.some((f: string) => f.toLowerCase().includes('product'))) {
      tables.push({
        name: 'products',
        columns: [
          { name: 'id', type: 'uuid', nullable: false, constraints: [] },
          { name: 'name', type: 'varchar(255)', nullable: false, constraints: [] },
          { name: 'description', type: 'text', nullable: true, constraints: [] },
          { name: 'price', type: 'decimal(10,2)', nullable: false, constraints: ['check(price > 0)'] },
          { name: 'category', type: 'varchar(100)', nullable: true, constraints: [] },
          { name: 'created_at', type: 'timestamp', nullable: false, defaultValue: 'NOW()', constraints: [] }
        ],
        primaryKey: 'id',
        indexes: ['category', 'price'],
        constraints: ['check(price > 0)']
      });
    }

    if (analysis.features.some((f: string) => f.toLowerCase().includes('order'))) {
      tables.push({
        name: 'orders',
        columns: [
          { name: 'id', type: 'uuid', nullable: false, constraints: [] },
          { name: 'user_id', type: 'uuid', nullable: false, constraints: ['foreign key references users(id)'] },
          { name: 'total', type: 'decimal(10,2)', nullable: false, constraints: ['check(total >= 0)'] },
          { name: 'status', type: 'varchar(50)', nullable: false, defaultValue: "'pending'", constraints: [] },
          { name: 'created_at', type: 'timestamp', nullable: false, defaultValue: 'NOW()', constraints: [] }
        ],
        primaryKey: 'id',
        indexes: ['user_id', 'status', 'created_at'],
        constraints: ['check(total >= 0)', 'foreign key(user_id) references users(id)']
      });
    }

    return tables;
  }

  private generateDatabaseRelationships(tables: DatabaseTable[]): DatabaseRelationship[] {
    const relationships: DatabaseRelationship[] = [];

    // Generate relationships based on foreign key constraints
    for (const table of tables) {
      for (const constraint of table.constraints) {
        if (constraint.includes('foreign key')) {
          const match = constraint.match(/foreign key\((\w+)\) references (\w+)\((\w+)\)/);
          if (match) {
            const [, fromColumn, toTable, toColumn] = match;
            relationships.push({
              name: `${table.name}_${fromColumn}_fk`,
              fromTable: table.name,
              toTable,
              fromColumn,
              toColumn,
              type: fromColumn.includes('_id') ? 'one-to-many' : 'one-to-one',
              onDelete: 'cascade'
            });
          }
        }
      }
    }

    return relationships;
  }

  private generateDatabaseIndexes(tables: DatabaseTable[], analysis: any): DatabaseIndex[] {
    const indexes: DatabaseIndex[] = [];

    for (const table of tables) {
      for (const indexColumn of table.indexes) {
        indexes.push({
          name: `${table.name}_${indexColumn}_idx`,
          table: table.name,
          columns: [indexColumn],
          type: 'btree',
          unique: false
        });
      }
    }

    // Add composite indexes for common queries
    if (tables.some(t => t.name === 'orders')) {
      indexes.push({
        name: 'orders_user_status_idx',
        table: 'orders',
        columns: ['user_id', 'status'],
        type: 'btree',
        unique: false
      });
    }

    return indexes;
  }

  private generateDatabaseMigrations(tables: DatabaseTable[], relationships: DatabaseRelationship[], indexes: DatabaseIndex[]): DatabaseMigration[] {
    const migrations: DatabaseMigration[] = [];

    // Initial migration for all tables
    let upSql = '';
    let downSql = '';

    for (const table of tables) {
      upSql += this.generateCreateTableSQL(table);
      downSql += `DROP TABLE IF EXISTS ${table.name};\n`;
    }

    for (const relationship of relationships) {
      upSql += this.generateForeignKeySQL(relationship);
      downSql += `ALTER TABLE ${relationship.fromTable} DROP CONSTRAINT ${relationship.name};\n`;
    }

    for (const index of indexes) {
      upSql += this.generateIndexSQL(index);
      downSql += `DROP INDEX IF EXISTS ${index.name};\n`;
    }

    migrations.push({
      version: '001',
      description: 'Initial database schema',
      up: upSql,
      down: downSql
    });

    return migrations;
  }

  private generateCreateTableSQL(table: DatabaseTable): string {
    const columns = table.columns.map(col =>
      `${col.name} ${col.type}${col.nullable ? '' : ' NOT NULL'}${col.defaultValue ? ` DEFAULT ${col.defaultValue}` : ''}`
    ).join(',\n  ');

    const constraints = table.constraints.map(constraint => `  ${constraint}`).join(',\n');

    return `CREATE TABLE ${table.name} (\n  ${columns}${constraints ? ',' : ''}\n${constraints}\n);\n\n`;
  }

  private generateForeignKeySQL(relationship: DatabaseRelationship): string {
    return `ALTER TABLE ${relationship.fromTable} ADD CONSTRAINT ${relationship.name} FOREIGN KEY (${relationship.fromColumn}) REFERENCES ${relationship.toTable}(${relationship.toColumn}) ON DELETE ${relationship.onDelete.toUpperCase()};\n\n`;
  }

  private generateIndexSQL(index: DatabaseIndex): string {
    const unique = index.unique ? 'UNIQUE ' : '';
    const columns = index.columns.join(', ');
    return `CREATE ${unique}INDEX ${index.name} ON ${index.table} USING ${index.type} (${columns});\n\n`;
  }

  private async generateMainAPI(analysis: any, architecture: ApplicationArchitecture, database: DatabaseSchema): Promise<APIBlueprint> {
    const endpoints: APIEndpoint[] = [];

    // Generate CRUD endpoints for each table
    for (const table of database.tables) {
      const resourceName = table.name.replace(/s$/, ''); // Remove plural 's'
      const basePath = `/${resourceName}s`;

      // GET /resources
      endpoints.push({
        path: basePath,
        method: 'GET',
        summary: `Get all ${resourceName}s`,
        description: `Retrieve a list of ${resourceName}s`,
        parameters: [
          {
            name: 'limit',
            in: 'query',
            required: false,
            schema: { type: 'integer', default: 10 },
            description: 'Maximum number of items to return'
          },
          {
            name: 'offset',
            in: 'query',
            required: false,
            schema: { type: 'integer', default: 0 },
            description: 'Number of items to skip'
          }
        ],
        responses: {
          200: {
            statusCode: 200,
            description: `List of ${resourceName}s`,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    data: {
                      type: 'array',
                      items: { $ref: `#/components/schemas/${resourceName}` }
                    },
                    total: { type: 'integer' },
                    limit: { type: 'integer' },
                    offset: { type: 'integer' }
                  }
                }
              }
            }
          }
        },
        security: ['bearerAuth'],
        tags: [resourceName]
      });

      // POST /resources
      endpoints.push({
        path: basePath,
        method: 'POST',
        summary: `Create a new ${resourceName}`,
        description: `Create a new ${resourceName} resource`,
        parameters: [], // No path/query parameters for POST
        requestBody: {
          required: true,
          content: {
            'application/json': {
              schema: { $ref: `#/components/schemas/${resourceName}Input` }
            }
          }
        },
        responses: {
          201: {
            statusCode: 201,
            description: `${resourceName} created successfully`,
            content: {
              'application/json': {
                schema: { $ref: `#/components/schemas/${resourceName}` }
              }
            }
          }
        },
        security: ['bearerAuth'],
        tags: [resourceName]
      });

      // GET /resources/{id}
      endpoints.push({
        path: `${basePath}/{id}`,
        method: 'GET',
        summary: `Get a specific ${resourceName}`,
        description: `Retrieve a specific ${resourceName} by ID`,
        parameters: [
          {
            name: 'id',
            in: 'path',
            required: true,
            schema: { type: 'string', format: 'uuid' },
            description: `${resourceName} ID`
          }
        ],
        responses: {
          200: {
            statusCode: 200,
            description: `${resourceName} details`,
            content: {
              'application/json': {
                schema: { $ref: `#/components/schemas/${resourceName}` }
              }
            }
          },
          404: {
            statusCode: 404,
            description: `${resourceName} not found`
          }
        },
        security: ['bearerAuth'],
        tags: [resourceName]
      });

      // PUT /resources/{id}
      endpoints.push({
        path: `${basePath}/{id}`,
        method: 'PUT',
        summary: `Update a ${resourceName}`,
        description: `Update an existing ${resourceName}`,
        parameters: [
          {
            name: 'id',
            in: 'path',
            required: true,
            schema: { type: 'string', format: 'uuid' },
            description: `${resourceName} ID`
          }
        ],
        requestBody: {
          required: true,
          content: {
            'application/json': {
              schema: { $ref: `#/components/schemas/${resourceName}Input` }
            }
          }
        },
        responses: {
          200: {
            statusCode: 200,
            description: `${resourceName} updated successfully`,
            content: {
              'application/json': {
                schema: { $ref: `#/components/schemas/${resourceName}` }
              }
            }
          }
        },
        security: ['bearerAuth'],
        tags: [resourceName]
      });

      // DELETE /resources/{id}
      endpoints.push({
        path: `${basePath}/{id}`,
        method: 'DELETE',
        summary: `Delete a ${resourceName}`,
        description: `Delete an existing ${resourceName}`,
        parameters: [
          {
            name: 'id',
            in: 'path',
            required: true,
            schema: { type: 'string', format: 'uuid' },
            description: `${resourceName} ID`
          }
        ],
        responses: {
          204: {
            statusCode: 204,
            description: `${resourceName} deleted successfully`
          }
        },
        security: ['bearerAuth'],
        tags: [resourceName]
      });
    }

    // Generate API models
    const models = this.generateAPIModels(database.tables);

    return {
      name: 'Main API',
      version: '1.0.0',
      basePath: '/api/v1',
      endpoints,
      models,
      authentication: {
        type: 'jwt',
        securitySchemes: {
          bearerAuth: {
            type: 'http',
            scheme: 'bearer',
            bearerFormat: 'JWT'
          }
        }
      },
      documentation: {
        title: 'Main API',
        version: '1.0.0',
        description: `API for ${analysis.description}`,
        servers: [
          {
            url: 'https://api.example.com/v1',
            description: 'Production server'
          }
        ]
      }
    };
  }

  private generateAPIModels(tables: DatabaseTable[]): APIModel[] {
    const models: APIModel[] = [];

    for (const table of tables) {
      const modelName = table.name.replace(/s$/, ''); // Remove plural

      // Main model
      models.push({
        name: modelName,
        type: 'data',
        schema: {
          type: 'object',
          properties: table.columns.reduce((props, col) => {
            props[col.name] = {
              type: this.mapSQLTypeToJSONType(col.type),
              description: `${col.name} field`
            };
            return props;
          }, {} as any),
          required: table.columns.filter(col => !col.nullable).map(col => col.name)
        },
        examples: [{}]
      });

      // Input model (for POST/PUT)
      const inputSchema = { ...models[models.length - 1].schema };
      if (inputSchema.properties) {
        delete inputSchema.properties.id; // Remove ID for input
        delete inputSchema.properties.created_at;
        delete inputSchema.properties.updated_at;
      }

      models.push({
        name: `${modelName}Input`,
        type: 'request',
        schema: inputSchema,
        examples: [{}]
      });
    }

    return models;
  }

  private mapSQLTypeToJSONType(sqlType: string): string {
    if (sqlType.includes('varchar') || sqlType.includes('text')) return 'string';
    if (sqlType.includes('int') || sqlType.includes('serial')) return 'integer';
    if (sqlType.includes('decimal') || sqlType.includes('numeric')) return 'number';
    if (sqlType.includes('boolean')) return 'boolean';
    if (sqlType.includes('timestamp') || sqlType.includes('date')) return 'string';
    if (sqlType.includes('uuid')) return 'string';
    return 'string';
  }

  private async generateServiceAPIs(architecture: ApplicationArchitecture, database: DatabaseSchema): Promise<APIBlueprint[]> {
    const apis: APIBlueprint[] = [];

    for (const component of architecture.components.filter(c => c.type === 'service')) {
      const api = await this.generateServiceAPI(component, database);
      apis.push(api);
    }

    return apis;
  }

  private async generateServiceAPI(component: ArchitectureComponent, database: DatabaseSchema): Promise<APIBlueprint> {
    // Generate service-specific API
    const endpoints: APIEndpoint[] = [];

    if (component.name.includes('auth')) {
      endpoints.push({
        path: '/auth/login',
        method: 'POST',
        summary: 'User login',
        description: 'Authenticate user credentials',
        parameters: [], // No path/query parameters for login
        requestBody: {
          required: true,
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  email: { type: 'string', format: 'email' },
                  password: { type: 'string' }
                },
                required: ['email', 'password']
              }
            }
          }
        },
        responses: {
          200: {
            statusCode: 200,
            description: 'Login successful',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    token: { type: 'string' },
                    user: { $ref: '#/components/schemas/User' }
                  }
                }
              }
            }
          }
        },
        security: [] as string[],
        tags: ['authentication']
      });
    }

    return {
      name: `${component.name} API`,
      version: '1.0.0',
      basePath: `/api/${component.name}`,
      endpoints,
      models: [],
      authentication: {
        type: 'jwt',
        securitySchemes: {}
      },
      documentation: {
        title: `${component.name} Service API`,
        version: '1.0.0',
        description: `API for ${component.name} service`,
        servers: []
      }
    };
  }

  private determineFrontendFramework(analysis: any): string {
    const frontendTech = analysis.technologies.frontend[0] || 'react';

    if (frontendTech.includes('vue')) return 'vue';
    if (frontendTech.includes('angular')) return 'angular';
    if (frontendTech.includes('svelte')) return 'svelte';
    return 'react';
  }

  private async generateFrontendComponents(analysis: any, apis: APIBlueprint[]): Promise<FrontendComponent[]> {
    const components: FrontendComponent[] = [];

    // Generate basic components
    components.push({
      name: 'App',
      type: 'layout',
      props: [],
      children: ['Header', 'Main', 'Footer'],
      styling: 'flex flex-col min-h-screen',
      logic: 'Main application component with routing'
    });

    components.push({
      name: 'Header',
      type: 'layout',
      props: [{ name: 'user', type: 'User', required: false, description: 'Current user object' }],
      children: ['Navigation', 'UserMenu'],
      styling: 'bg-white shadow-sm border-b',
      logic: 'Application header with navigation'
    });

    // Generate components based on API endpoints
    for (const api of apis) {
      for (const endpoint of api.endpoints) {
        if (endpoint.method === 'GET' && endpoint.path.includes('/')) {
          const resourceName = endpoint.path.split('/')[1];
          components.push({
            name: `${resourceName}List`,
            type: 'list',
            props: [
              { name: 'items', type: `${resourceName}[]`, required: true, description: `Array of ${resourceName} items to display` },
              { name: 'loading', type: 'boolean', required: false, defaultValue: false, description: 'Loading state indicator' }
            ],
            children: [`${resourceName}Item`],
            styling: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
            logic: `List component for ${resourceName} resources`
          });
        }
      }
    }

    return components;
  }

  private async generateFrontendPages(analysis: any, components: FrontendComponent[]): Promise<FrontendPage[]> {
    const pages: FrontendPage[] = [];

    pages.push({
      name: 'Home',
      route: '/',
      layout: 'App',
      components: ['Header', 'Hero', 'Features'],
      dataRequirements: [],
      permissions: []
    });

    pages.push({
      name: 'Dashboard',
      route: '/dashboard',
      layout: 'App',
      components: ['Header', 'Sidebar', 'DashboardContent'],
      dataRequirements: ['user', 'stats'],
      permissions: ['authenticated']
    });

    // Generate CRUD pages based on components
    const listComponents = components.filter(c => c.type === 'list');
    for (const listComp of listComponents) {
      const resourceName = listComp.name.replace('List', '');
      pages.push({
        name: `${resourceName}List`,
        route: `/${resourceName.toLowerCase()}s`,
        layout: 'App',
        components: ['Header', listComp.name, 'Pagination'],
        dataRequirements: [`${resourceName.toLowerCase()}s`],
        permissions: ['authenticated']
      });
    }

    return pages;
  }

  private generateFrontendRouting(pages: FrontendPage[]): FrontendRouting {
    const routes: FrontendRoute[] = pages.map(page => ({
      path: page.route,
      component: page.name,
      guards: page.permissions.includes('authenticated') ? ['authGuard'] : []
    }));

    return {
      type: 'client-side',
      routes,
      guards: [
        {
          name: 'authGuard',
          logic: 'Check if user is authenticated, redirect to login if not',
          redirectTo: '/login'
        }
      ]
    };
  }

  private generateStateManagement(analysis: any, framework: string): FrontendState {
    if (framework === 'react') {
      return {
        type: 'zustand',
        stores: [
          {
            name: 'userStore',
            state: { user: null, loading: false },
            actions: ['setUser', 'logout', 'setLoading']
          },
          {
            name: 'uiStore',
            state: { theme: 'light', sidebarOpen: true },
            actions: ['toggleTheme', 'toggleSidebar']
          }
        ],
        actions: []
      };
    }

    return {
      type: 'redux',
      stores: [],
      actions: []
    };
  }

  private generateFrontendStyling(framework: string): FrontendStyling {
    return {
      framework: 'tailwind',
      theme: {
        colors: {
          primary: '#3B82F6',
          secondary: '#64748B',
          success: '#10B981',
          warning: '#F59E0B',
          error: '#EF4444'
        },
        typography: {
          fontFamily: 'Inter, sans-serif',
          fontSize: {
            base: '1rem',
            lg: '1.125rem',
            xl: '1.25rem'
          }
        },
        spacing: {
          1: '0.25rem',
          2: '0.5rem',
          4: '1rem',
          8: '2rem'
        },
        breakpoints: {
          sm: '640px',
          md: '768px',
          lg: '1024px',
          xl: '1280px'
        }
      },
      globalStyles: 'Modern, clean design with consistent spacing and typography',
      componentStyles: {}
    };
  }

  private generateFrontendBuild(framework: string): FrontendBuild {
    return {
      bundler: 'vite',
      config: {
        build: {
          outDir: 'dist',
          sourcemap: true
        },
        server: {
          port: 3000,
          host: true
        }
      },
      scripts: {
        dev: 'vite',
        build: 'vite build',
        preview: 'vite preview',
        test: 'vitest',
        lint: 'eslint src --ext .ts,.tsx'
      },
      optimizations: [
        'Code splitting',
        'Tree shaking',
        'Asset optimization',
        'Compression'
      ]
    };
  }

  private determineDeploymentPlatform(analysis: any): DeploymentConfig['platform'] {
    const deploymentPrefs = analysis.technologies.deployment;

    if (deploymentPrefs.includes('kubernetes')) return 'kubernetes';
    if (deploymentPrefs.includes('aws')) return 'aws';
    if (deploymentPrefs.includes('azure')) return 'azure';
    if (deploymentPrefs.includes('gcp')) return 'gcp';
    if (deploymentPrefs.includes('vercel')) return 'vercel';
    if (deploymentPrefs.includes('heroku')) return 'heroku';

    return 'docker';
  }

  private async generateDeploymentServices(architecture: ApplicationArchitecture, platform: DeploymentConfig['platform']): Promise<DeploymentService[]> {
    const services: DeploymentService[] = [];

    for (const component of architecture.components) {
      services.push({
        name: component.name,
        image: this.getComponentImage(component, platform),
        ports: this.getComponentPorts(component),
        environment: this.getComponentEnvironment(component),
        volumes: this.getComponentVolumes(component),
        dependsOn: component.dependencies,
        healthcheck: {
          test: ['CMD', 'curl', '-f', `http://localhost:${this.getComponentPorts(component)[0] || 3000}/health`],
          interval: '30s',
          timeout: '10s',
          retries: 3,
          startPeriod: '40s'
        }
      });
    }

    return services;
  }

  private getComponentImage(component: ArchitectureComponent, platform: DeploymentConfig['platform']): string {
    const tech = component.technology.toLowerCase();

    if (tech.includes('node')) return 'node:18-alpine';
    if (tech.includes('python')) return 'python:3.11-slim';
    if (tech.includes('java')) return 'openjdk:17-jdk-slim';
    if (tech.includes('dotnet')) return 'mcr.microsoft.com/dotnet/sdk:8.0';
    if (tech.includes('go')) return 'golang:1.21-alpine';
    if (tech.includes('postgres')) return 'postgres:15-alpine';
    if (tech.includes('redis')) return 'redis:7-alpine';
    if (tech.includes('nginx')) return 'nginx:alpine';

    return 'node:18-alpine'; // Default
  }

  private getComponentPorts(component: ArchitectureComponent): number[] {
    switch (component.type) {
      case 'frontend': return [3000];
      case 'backend': return [8080];
      case 'database':
        if (component.technology.includes('postgres')) return [5432];
        if (component.technology.includes('redis')) return [6379];
        return [3306]; // MySQL default
      case 'api': return [8080];
      default: return [3000];
    }
  }

  private getComponentEnvironment(component: ArchitectureComponent): { [key: string]: string } {
    const env: { [key: string]: string } = {};

    if (component.type === 'database') {
      if (component.technology.includes('postgres')) {
        env['POSTGRES_DB'] = 'app_db';
        env['POSTGRES_USER'] = 'app_user';
        env['POSTGRES_PASSWORD'] = '${DB_PASSWORD}';
      }
    }

    if (component.type === 'backend' || component.type === 'api') {
      env['NODE_ENV'] = 'production';
      env['PORT'] = this.getComponentPorts(component)[0].toString();
      env['DATABASE_URL'] = '${DATABASE_URL}';
      env['JWT_SECRET'] = '${JWT_SECRET}';
    }

    return env;
  }

  private getComponentVolumes(component: ArchitectureComponent): string[] {
    if (component.type === 'database') {
      return ['db_data:/var/lib/postgresql/data'];
    }
    return [];
  }

  private generateDeploymentInfrastructure(architecture: ApplicationArchitecture, platform: DeploymentConfig['platform']): DeploymentInfrastructure {
    const volumes: string[] = [];
    const secrets: string[] = ['db_password', 'jwt_secret'];
    const configs: { [key: string]: string } = {};

    // Add volumes for databases
    if (architecture.components.some(c => c.type === 'database')) {
      volumes.push('db_data');
    }

    // Add volumes for file storage
    if (architecture.components.some(c => c.responsibilities.some(r => r.includes('File') || r.includes('Upload')))) {
      volumes.push('uploads');
    }

    // Generate nginx config if web app
    if (architecture.components.some(c => c.type === 'frontend')) {
      configs['nginx.conf'] = this.generateNginxConfig();
    }

    return {
      network: 'app_network',
      volumes,
      secrets,
      configs
    };
  }

  private generateDeploymentMonitoring(platform: DeploymentConfig['platform']): DeploymentMonitoring {
    return {
      logging: {
        driver: 'json-file',
        options: {
          'max-size': '10m',
          'max-file': '3'
        },
        format: 'json'
      },
      metrics: {
        provider: 'prometheus',
        endpoints: ['/metrics'],
        customMetrics: ['response_time', 'error_rate', 'throughput']
      },
      tracing: {
        provider: 'jaeger',
        samplingRate: 0.1,
        serviceName: 'app'
      },
      alerting: {
        provider: 'slack',
        channels: ['alerts'],
        rules: [
          {
            name: 'High Error Rate',
            condition: 'error_rate > 0.05',
            severity: 'high',
            channels: ['alerts', 'dev-team']
          },
          {
            name: 'High Response Time',
            condition: 'response_time > 5s',
            severity: 'medium',
            channels: ['alerts']
          }
        ]
      }
    };
  }

  private generateDeploymentScaling(analysis: any, architecture: ApplicationArchitecture): DeploymentScaling {
    const scalability = analysis.constraints?.scalability || 'medium';

    let minInstances = 1;
    let maxInstances = 3;
    let targetCPU = 70;
    let targetMemory = 80;

    if (scalability === 'high') {
      minInstances = 2;
      maxInstances = 10;
      targetCPU = 60;
      targetMemory = 70;
    } else if (scalability === 'low') {
      minInstances = 1;
      maxInstances = 1;
      targetCPU = 80;
      targetMemory = 85;
    }

    return {
      type: 'horizontal',
      minInstances,
      maxInstances,
      targetCPU,
      targetMemory,
      scalingRules: [
        {
          metric: 'cpu_utilization',
          operator: '>',
          threshold: targetCPU,
          action: 'scale-up',
          cooldown: 300
        },
        {
          metric: 'memory_utilization',
          operator: '>',
          threshold: targetMemory,
          action: 'scale-up',
          cooldown: 300
        }
      ]
    };
  }

  private generateNginxConfig(): string {
    return `
upstream app_backend {
    server backend:8080;
}

server {
    listen 80;
    server_name localhost;

    location / {
        proxy_pass http://app_frontend:3000;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
    }

    location /api/ {
        proxy_pass http://app_backend;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
    }
}
    `.trim();
  }

  private async generateReadme(analysis: any): Promise<string> {
    const appName = this.generateApplicationName({ description: analysis.description } as any);

    return `# ${appName}

${analysis.description}

## Features

${analysis.features.map((feature: string) => `- ${feature}`).join('\n')}

## Technology Stack

### Frontend
${analysis.technologies.frontend.map((tech: string) => `- ${tech}`).join('\n')}

### Backend
${analysis.technologies.backend.map((tech: string) => `- ${tech}`).join('\n')}

### Database
${analysis.technologies.database.map((tech: string) => `- ${tech}`).join('\n')}

## Getting Started

### Prerequisites
- Node.js 18+
- Docker & Docker Compose
- Git

### Installation

1. Clone the repository
\`\`\`bash
git clone <repository-url>
cd ${appName.toLowerCase().replace(/\s+/g, '-')}
\`\`\`

2. Install dependencies
\`\`\`bash
npm install
\`\`\`

3. Start the development environment
\`\`\`bash
docker-compose up -d
npm run dev
\`\`\`

4. Open [http://localhost:3000](http://localhost:3000) in your browser

## API Documentation

API documentation is available at \`/api/docs\` when the server is running.

## Deployment

See [Deployment Guide](./docs/deployment.md) for production deployment instructions.

## Contributing

1. Fork the repository
2. Create a feature branch
3. Make your changes
4. Run tests
5. Submit a pull request

## License

This project is licensed under the MIT License - see the [LICENSE](LICENSE) file for details.
`;
  }

  private generateApplicationName(requirements: ApplicationRequirements): string {
    // Generate a meaningful name based on description and features
    const words = requirements.description.toLowerCase().split(' ');
    const keyWords = words.filter(word =>
      word.length > 3 &&
      !['that', 'with', 'this', 'will', 'should', 'would', 'could', 'application', 'system', 'platform'].includes(word)
    );

    if (keyWords.length >= 2) {
      return keyWords.slice(0, 2).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    // Fallback naming
    const domains = ['App', 'Hub', 'Platform', 'System', 'Manager'];
    return `Smart ${domains[Math.floor(Math.random() * domains.length)]}`;
  }

  // Placeholder methods that would be implemented
  private generateAPIDocs(apis: APIBlueprint[]): string {
    return `# API Documentation

This document describes the API endpoints available in the application.

## Endpoints

${apis.map(api => api.endpoints.map(endpoint =>
  `### ${endpoint.method} ${api.basePath}${endpoint.path}\n${endpoint.summary}\n`
).join('')).join('')}

## Authentication

All API endpoints require authentication using JWT tokens.
`;
  }

  private generateDeploymentGuide(deployment: DeploymentConfig): string {
    return `# Deployment Guide

## Local Development

\`\`\`bash
docker-compose up -d
npm run dev
\`\`\`

## Production Deployment

### Using Docker

\`\`\`bash
docker build -t ${deployment.services[0]?.name || 'app'} .
docker run -p 8080:8080 ${deployment.services[0]?.name || 'app'}
\`\`\`

### Using Docker Compose

\`\`\`bash
docker-compose -f docker-compose.prod.yml up -d
\`\`\`

## Environment Variables

- \`NODE_ENV\`: Environment (development/production)
- \`PORT\`: Port to run the application on
- \`DATABASE_URL\`: Database connection string
- \`JWT_SECRET\`: JWT signing secret

## Monitoring

The application includes built-in monitoring with Prometheus and Grafana.
Access the monitoring dashboard at \`/monitoring\` when deployed.
`;
  }

  private generateArchitectureDiagram(architecture: ApplicationArchitecture): string {
    return `# Architecture Diagram

## Overview

This application follows a ${architecture.pattern} architecture pattern.

## Components

${architecture.components.map(comp =>
  `### ${comp.name} (${comp.type})
- **Technology**: ${comp.technology}
- **Responsibilities**: ${comp.responsibilities.join(', ')}
- **Scalability**: ${comp.scalability}
- **Dependencies**: ${comp.dependencies.join(', ') || 'None'}
`
).join('\n')}

## Data Flow

${architecture.dataFlow.workflows.map(workflow =>
  `### ${workflow.name}
${workflow.steps.map(step => `- ${step.name}: ${step.action} (${step.component})`).join('\n')}
`
).join('\n')}
`;
  }

  private async generateUserGuide(analysis: any): Promise<string> {
    return `# User Guide

## Welcome

Welcome to your new application! This guide will help you get started.

## Features

${analysis.features.map((feature: string) => `### ${feature}\n\nDescription of how to use this feature.\n`).join('\n')}

## Getting Started

1. Create an account or log in
2. Explore the dashboard
3. Start using the features

## Support

If you need help, please contact support or refer to the FAQ section.
`;
  }

  private generateDeveloperGuide(architecture: ApplicationArchitecture, frontend: FrontendBlueprint): string {
    return `# Developer Guide

## Architecture

This application uses a ${architecture.pattern} architecture.

## Technology Stack

- **Frontend**: ${frontend.framework}
- **Backend**: ${architecture.components.find(c => c.type === 'backend')?.technology}
- **Database**: ${architecture.components.find(c => c.type === 'database')?.technology}

## Development Setup

1. Clone the repository
2. Install dependencies: \`npm install\`
3. Set up environment variables
4. Run database migrations
5. Start development server: \`npm run dev\`

## Code Structure

- \`src/components/\`: React components
- \`src/services/\`: API services and utilities
- \`src/stores/\`: State management
- \`backend/\`: Backend API code

## Testing

Run tests with: \`npm test\`

## Deployment

See deployment documentation for production setup.
`;
  }

  private generateSecurityGuide(architecture: ApplicationArchitecture): string {
    const security = architecture.securityArchitecture;

    return `# Security Guide

## Authentication

This application uses ${security.authentication} for user authentication.

## Authorization

${security.authorization} controls access to resources.

## Data Protection

${security.encryption}

## Security Features

${security.securityFeatures.map(feature => `- ${feature}`).join('\n')}

## Best Practices

- Always validate user input
- Use HTTPS in production
- Regularly update dependencies
- Monitor for security vulnerabilities
- Implement proper logging and auditing
`;
  }

  private generatePerformanceGuide(architecture: ApplicationArchitecture, deployment: DeploymentConfig): string {
    return `# Performance Guide

## Architecture Considerations

The application uses a ${architecture.pattern} architecture designed for ${architecture.scalabilityStrategy.toLowerCase()}.

## Scaling

- **Type**: ${deployment.scaling.type}
- **Instances**: ${deployment.scaling.minInstances} - ${deployment.scaling.maxInstances}
- **Auto-scaling**: Enabled based on CPU and memory usage

## Optimization Tips

1. **Database**: Use proper indexing and query optimization
2. **Caching**: Implement Redis caching for frequently accessed data
3. **CDN**: Use CDN for static assets in production
4. **Monitoring**: Regular performance monitoring and bottleneck identification

## Performance Metrics

- Response Time: <500ms target
- CPU Usage: <${deployment.scaling.targetCPU}% target
- Memory Usage: <${deployment.scaling.targetMemory}% target
- Error Rate: <1% target
`;
  }
}

export const autonomousApplicationGenerator = new AutonomousApplicationGenerator();